import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { EmptyState } from "../components/EmptyState";
import { GuidedIcpFlow } from "../components/GuidedIcpFlow";
import { IcpRow } from "../components/IcpRow";
import { IcpHistoryRow } from "../components/IcpHistoryRow";
import { Select } from "../components/Select";
import { SegmentedControl } from "../components/SegmentedControl";
import { TemplatePanel } from "../components/TemplatePanel";
import { TodoForm } from "../components/TodoForm";
import { TodoRow } from "../components/TodoRow";
import { IconTodos } from "../components/icons";
import { useHomes } from "../hooks/useSettings";
import { useDoneTodos, useTodoMutations } from "../hooks/useTodos";
import { useUnifiedQueue } from "../hooks/useUnifiedQueue";
import { fadeUp, pop, stagger, tap } from "../motion";

export function TodosPage() {
  const [view, setView] = useState<"open" | "done">("open");
  const [homeId, setHomeId] = useState("");
  const [tag, setTag] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [flowId, setFlowId] = useState<string | null>(null);
  const { data: homes } = useHomes();
  const queue = useUnifiedQueue(homeId || undefined, tag || undefined);
  const done = useDoneTodos(view === "done");
  const { create, complete, remove } = useTodoMutations();

  const homeName = (id: string | null) => homes?.find((h) => h.id === id)?.name;
  const homeIndex = (id: string | null) => (id ? homes?.findIndex((h) => h.id === id) : undefined);
  // Every tag on open to-dos in the current home scope (the hook derives it
  // from the unfiltered list, so tags on icp-group to-dos are included and the
  // options stay stable while a filter is active).
  const tags = queue.data?.tagOptions ?? [];
  const buckets = queue.data?.buckets ?? [];
  // Sticky queue headers: the frosted bar + taller padding only apply while a header is
  // actually pinned at the top of the scroll container - no background at rest.
  useEffect(() => {
    const content = document.querySelector<HTMLElement>(".content");
    if (!content) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const top = content.getBoundingClientRect().top;
      document.querySelectorAll<HTMLElement>(".queue-head").forEach((head) => {
        const r = head.getBoundingClientRect();
        head.classList.toggle("is-stuck", r.top <= top + 1 && r.bottom > top);
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    content.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      content.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  });

  return (
    <>
      <motion.div className="page-head" variants={fadeUp} initial="hidden" animate="show">
        <h1>To-dos</h1>
      </motion.div>

      <motion.div className="toolbar" variants={fadeUp} initial="hidden" animate="show" transition={{ delay: 0.05 }}>
        <SegmentedControl
          ariaLabel="View"
          layoutId="todos-view"
          options={[{ value: "open", label: "Open" }, { value: "done", label: "Done" }]}
          value={view}
          onChange={(v) => setView(v === "done" ? "done" : "open")}
        />
        <Select
          ariaLabel="Filter by home"
          value={homeId}
          onChange={setHomeId}
          style={{ width: "auto" }}
          options={[{ value: "", label: "All homes" }, ...(homes ?? []).map((h) => ({ value: h.id, label: h.name }))]}
        />
        {tags.length > 0 ? (
          <Select
            ariaLabel="Filter by tag"
            value={tag}
            onChange={setTag}
            style={{ width: "auto" }}
            options={[{ value: "", label: "All tags" }, ...tags.map((t) => ({ value: t, label: t }))]}
          />
        ) : null}
        <div className="toolbar-actions">
          <Link className="btn btn-ghost" to="/forecast">Forecast →</Link>
          <motion.button type="button" className="btn btn-primary" onClick={() => setShowForm((s) => !s)} {...tap}>
            {showForm ? "Close" : "New to-do"}
          </motion.button>
        </div>
      </motion.div>

      <AnimatePresence initial={false}>
        {showForm ? <TodoForm homes={homes ?? []} onCancel={() => setShowForm(false)} onSubmit={(v) => create.mutateAsync(v)} /> : null}
      </AnimatePresence>

      {view === "done" ? (
        <>
          {done.isLoading ? <p className="muted">Loading…</p> : null}
          {done.isError ? <p className="error">Failed to load completed to-dos. Retry.</p> : null}
          {!done.isLoading && !done.isError && (done.data?.length ?? 0) === 0 ? (
            <motion.div variants={pop} initial="hidden" animate="show">
              <EmptyState icon={<IconTodos />} title="Nothing completed yet" />
            </motion.div>
          ) : null}
          <motion.ul className="todo-list" variants={stagger(0.04)} initial="hidden" animate="show">
            <AnimatePresence initial={false}>
              {(done.data ?? []).map((item) =>
                item.kind === "icp" ? (
                  <IcpHistoryRow key={item.icp.id} item={item.icp} homeName={homeName(item.icp.homeId)} />
                ) : (
                  <TodoRow
                    key={item.todo.id}
                    todo={item.todo}
                    homeName={homeName(item.todo.homeId)}
                    homeIndex={homeIndex(item.todo.homeId)}
                    onComplete={(d) => complete.mutate({ id: item.todo.id, done: d })}
                    onDelete={() => remove.mutate(item.todo.id)}
                  />
                ),
              )}
            </AnimatePresence>
          </motion.ul>
        </>
      ) : (
        <>
          {queue.isLoading ? <p className="muted">Loading…</p> : null}
          {queue.isError ? <p className="error">Failed to load to-dos. Retry.</p> : null}
          {!queue.isLoading && !queue.isError && buckets.length === 0 ? (
            <motion.div variants={pop} initial="hidden" animate="show">
              <EmptyState
                icon={<IconTodos />}
                title="Nothing here"
                action={<motion.button type="button" className="btn btn-primary" {...tap} onClick={() => setShowForm(true)}>New to-do</motion.button>}
              />
            </motion.div>
          ) : null}
          {buckets.map((bucket) => {
            // Urgency colour on the time-frame header (design #1): Overdue = danger, Today = accent.
            const isOverdue = bucket.key === "overdue";
            const isToday = bucket.key === "today";
            return (
            <motion.section key={bucket.key} className="queue-section" variants={fadeUp} initial="hidden" animate="show">
              <header className="queue-head">
                <h2 className={`queue-title${isOverdue ? " is-overdue" : isToday ? " is-today" : ""}`}>{bucket.label}</h2>
                <span className={`chip${isOverdue ? " chip-danger" : isToday ? " chip-accent" : ""}`}>{bucket.icp.length + bucket.docs.length}</span>
              </header>
              {bucket.icp.length > 0 ? (
                <>
                  <p className="queue-subhead">ICP</p>
                  <motion.ul className="todo-list" variants={stagger(0.04)} initial="hidden" animate="show">
                    <AnimatePresence initial={false}>
                      {bucket.icp.map((item) =>
                        "kind" in item ? (
                          <IcpRow key={item.residentRef} item={item} onContinue={() => setFlowId(item.residentRef)} />
                        ) : (
                          <TodoRow
                            key={item.id}
                            todo={item}
                            homeName={homeName(item.homeId)}
                            homeIndex={homeIndex(item.homeId)}
                            onComplete={(d) => complete.mutate({ id: item.id, done: d })}
                            onDelete={() => remove.mutate(item.id)}
                          />
                        ),
                      )}
                    </AnimatePresence>
                  </motion.ul>
                </>
              ) : null}
              {bucket.docs.length > 0 ? (
                <>
                  <p className="queue-subhead">Documentation</p>
                  <motion.ul className="todo-list" variants={stagger(0.04)} initial="hidden" animate="show">
                    <AnimatePresence initial={false}>
                      {bucket.docs.map((todo) => (
                        <TodoRow
                          key={todo.id}
                          todo={todo}
                          homeName={homeName(todo.homeId)}
                          homeIndex={homeIndex(todo.homeId)}
                          onComplete={(d) => complete.mutate({ id: todo.id, done: d })}
                          onDelete={() => remove.mutate(todo.id)}
                        />
                      ))}
                    </AnimatePresence>
                  </motion.ul>
                </>
              ) : null}
            </motion.section>
            );
          })}
        </>
      )}

      <TemplatePanel />
      <GuidedIcpFlow residentId={flowId} onClose={() => setFlowId(null)} />
    </>
  );
}